/**
 * The TWO downtime numbers, named once — TRBUD-222, DEC-03 of the 9 September 2026
 * demo.
 *
 * ## The ruling
 *
 * Brett, live in the demo, on what he reads a fleet report for:
 *
 *   "I look at how much our equipment is down over a month … they were down total
 *    20% of the time, for example, regardless of what happened in the segments
 *    within that number, right? Because it was still down."
 *
 * And, watching a stoppage being recorded: *"so when you stop, does it keep adding
 * up that number?"* The answer must be YES for asset downtime and NO for active
 * repair time, and the screen must make it impossible to guess wrong.
 *
 * So there are exactly two quantities and they are never the same number:
 *
 * | Quantity | Accrues | Reduced by a stoppage | Source |
 * | --- | --- | --- | --- |
 * | **Asset downtime (total)** | from the moment the asset goes down until it returns to service | **NEVER** | the `asset_downtime` interval |
 * | **Active repair time (net of stoppages)** | only while someone could be working | yes, by every stoppage | downtime minus `work_order_stoppage` |
 *
 * Stoppages exist so that waiting on a part does not count against the REPAIR-TIME
 * ESTIMATE. They must not reduce asset downtime. An under-reported downtime figure
 * reproduces the founding defect of this project — MaintainX reporting completion
 * figures over 100% that nobody could explain — except in the reassuring direction,
 * which is worse, because a fleet that never breaks down looks fine.
 *
 * ## Why constants rather than string literals at each call site
 *
 * Brett's written confirmation of the exact wording is still pending, so the labels
 * have to be cheap to rename — one edit here, every screen follows. That is also
 * what stops the two numbers drifting into two different sets of words on two
 * screens, which is how "Off road" on the work order and "Downtime 30d" on the
 * Assets table came to look like different quantities when they are the same one.
 *
 * Browser-safe: strings only, like the rest of `vocabulary.ts`.
 */

/**
 * The GROSS number. Accrues continuously from down to back-in-service, through
 * every stoppage.
 *
 * Used by: the work-order stoppage card, the Assets table's 30-day column, the
 * asset record's 30-day and lifetime figures, the Reports downtime chart and the
 * category-spend downtime columns.
 */
export const ASSET_DOWNTIME_LABEL = "Asset downtime (total)";

/** The same quantity where a column header has no room for the parenthetical. */
export const ASSET_DOWNTIME_LABEL_SHORT = "Asset downtime";

/** Work-order readouts name the visit whose single interval the packages share. */
export const VISIT_DOWNTIME_LABEL = "Visit downtime";
export const VISIT_DOWNTIME_BASIS_NOTE =
  "Visit downtime keeps accruing while the truck is off the road. Waiting is counted " +
  "only inside its downtime intervals, with overlapping interruptions counted once. " +
  "Active repair time is visit downtime minus that waiting; it is not logged labour.";

/*
 * ── How a truck stop ended (TRBUD-338) ─────────────────────────────────────
 *
 * Eden's ruling of 5 October 2026: visit downtime is reported once, under the
 * ticket, and each job's screen shows it with a "held up by" note naming the
 * job that finished last. The asset record and the work-order screen word the
 * same stop with these constants, through one component.
 */
export const HELD_UP_BY_LABEL = "Held up by";
export const HELD_BY_LABEL = "Held by";
export const FINISHED_LAST_NOTE = "finished last";
export const ENDED_BY_COMPLETION_LABEL = "Ended by completion.";
export const ENDED_BY_CANCELLATION_LABEL = "Ended by cancellation.";
export const RELEASED_BY_HAND_LABEL = "Released by hand by";
export const ENDING_UNKNOWN_NOTE = "Ending job unknown; no recorded evidence.";
export const EVERY_STOP_ON_ASSET_RECORD = "Every stop is on the asset record";

/** The NET number. Downtime minus every stoppage. Never an availability figure. */
export const ACTIVE_REPAIR_LABEL = "Active repair time (net of stoppages)";

/** How much of the downtime window nobody could work in, and why. */
export const STOPPED_WAITING_LABEL = "Stopped (waiting)";

/**
 * The sentence that has to sit beside the pair wherever both are shown.
 *
 * Prose rather than a tooltip, deliberately: DEC-08 of the same demo ruled that
 * report accuracy outranks click count, and a caveat behind a hover is a caveat
 * most readers never see.
 */
export const DOWNTIME_BASIS_NOTE =
  "Asset downtime keeps accruing while the truck is off the road. Waiting is counted " +
  "only inside its downtime intervals, with overlapping interruptions counted once. " +
  "Active repair time is downtime minus that waiting; it is not logged labour.";

/** The same claim, for a screen that shows the GROSS figure on its own. */
export const ASSET_DOWNTIME_ONLY_NOTE =
  "Total time off the road, stoppages included. Not net of stoppages.";

/*
 * ── Two constants that are NOT here, and why ───────────────────────────────
 *
 * `ACTIVE_REPAIR_LABEL_SHORT` and `ACTIVE_REPAIR_ESTIMATE_NOTE` were written with
 * this file and are held back with the rest of DEC-03's estimate work (decision
 * D8). Their only readers were the board card and the insights query that
 * computes a completion-time estimate, and neither ships yet.
 *
 * They are absent rather than present-and-unused on purpose: this batch removed a
 * `searchComments` capability that sat false and unread for its whole life, and
 * the lesson there was that a dead export beside live ones misleads the next
 * reader into thinking it governs something. Two string constants are cheap to
 * write again on the day the estimate lands.
 */
